import React, { useEffect, useMemo, useRef, useState } from "react";
import { Minus, Navigation, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import L from "leaflet";
import { CircleMarker, MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || "";
const MAPBOX_STYLE = "mapbox://styles/mapbox/streets-v12";
const MAPBOX_JS = "https://api.mapbox.com/mapbox-gl-js/v3.11.0/mapbox-gl.js";
const MAPBOX_CSS = "https://api.mapbox.com/mapbox-gl-js/v3.11.0/mapbox-gl.css";
let mapboxPromise = null;

function loadMapbox() {
  if (window.mapboxgl) return Promise.resolve(window.mapboxgl);
  if (mapboxPromise) return mapboxPromise;
  mapboxPromise = new Promise((resolve, reject) => {
    if (!document.querySelector("link[data-mapbox-gl]")) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = MAPBOX_CSS;
      link.dataset.mapboxGl = "true";
      document.head.appendChild(link);
    }
    const script = document.createElement("script");
    script.src = MAPBOX_JS;
    script.async = true;
    script.onload = () => (window.mapboxgl ? resolve(window.mapboxgl) : reject(new Error("Mapbox GL did not initialize")));
    script.onerror = () => reject(new Error("Mapbox GL could not be loaded"));
    document.head.appendChild(script);
  }).catch((error) => {
    mapboxPromise = null;
    throw error;
  });
  return mapboxPromise;
}
const VEHICLE_MARKER_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 44 44"><circle cx="22" cy="22" r="20" fill="#fbbf24" stroke="#fff" stroke-width="4"/><g transform="translate(10 10)" fill="none" stroke="#0f172a" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m21 8-2 2-1.5-3.7A2 2 0 0 0 15.646 5H8.4a2 2 0 0 0-1.903 1.257L5 10 3 8"/><path d="M7 14h.01M17 14h.01"/><rect width="18" height="8" x="3" y="10" rx="2"/><path d="M5 18v2M19 18v2"/></g></svg>`;
const VEHICLE_MARKER_URL = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(VEHICLE_MARKER_SVG)}`;
const PERSON_MARKER_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="42" height="50" viewBox="0 0 42 50"><g fill="#fbbf24" stroke="#fff" stroke-width="3" paint-order="stroke" stroke-linejoin="round"><circle cx="21" cy="12" r="7"/><path d="M11 25c0-3.4 4.2-5.6 10-5.6s10 2.2 10 5.6v8.2c0 1.3-1 2.3-2.3 2.3h-2.4l-.9 8.6c-.1 1-.9 1.7-1.9 1.7h-5c-1 0-1.8-.7-1.9-1.7l-.9-8.6h-2.4c-1.3 0-2.3-1-2.3-2.3V25z"/></g></svg>`;
const PICKUP_MARKER_SIZE = 142;
const PICKUP_MARKER_HEIGHT = 190;

function escapeSvgText(value) {
  return String(value).replace(/[&<>"']/g, (character) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&apos;",
  })[character]);
}

function pickupMarkerSvg(callout) {
  const hasEta = Number.isFinite(Number(callout?.etaMinutes));
  const mainText = hasEta ? String(Math.max(0, Math.round(Number(callout.etaMinutes)))) : escapeSvgText(callout?.label || "");
  const text = hasEta
    ? `<text x="71" y="70" text-anchor="middle" fill="#fff" font-family="Arial,sans-serif" font-size="48" font-weight="500">${mainText}</text><text x="71" y="101" text-anchor="middle" fill="#fff" font-family="Arial,sans-serif" font-size="21" font-weight="500">${escapeSvgText(callout.unit || "")}</text>`
    : `<text x="71" y="82" text-anchor="middle" fill="#fff" font-family="Arial,sans-serif" font-size="30" font-weight="600">${mainText}</text>`;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${PICKUP_MARKER_SIZE}" height="${PICKUP_MARKER_HEIGHT}" viewBox="0 0 142 190"><defs><filter id="shadow" x="-40%" y="-30%" width="180%" height="190%"><feDropShadow dx="0" dy="3" stdDeviation="3" flood-color="#172033" flood-opacity=".22"/></filter></defs><path d="M71 124v51" fill="none" stroke="#ff5038" stroke-width="8" stroke-linecap="round"/><circle cx="71" cy="67" r="64" fill="#ff5038" filter="url(#shadow)"/>${text}<circle cx="71" cy="179" r="10" fill="#fff" filter="url(#shadow)"/><circle cx="71" cy="179" r="5.5" fill="#ff5038"/></svg>`;
}

function pickupMarkerUrl(callout) {
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(pickupMarkerSvg(callout))}`;
}

const leafletVehicleIcon = new L.DivIcon({
  html: VEHICLE_MARKER_SVG,
  className: "",
  iconSize: [44, 44],
  iconAnchor: [22, 22],
});
let googleMapsPromise = null;
let googleMapsPromiseKey = "";

async function getGoogleMapsLibraries() {
  const googleMaps = window.google?.maps;
  if (!googleMaps) throw new Error("Google Maps API did not initialize");

  // The direct Maps JavaScript API script can expose the classic constructors
  // without importLibrary. Prefer the modern loader when available, but don't
  // treat that optional helper as proof that the API failed to load.
  let libraries;
  if (typeof googleMaps.importLibrary === "function") {
    const [mapsLibrary, markerLibrary, coreLibrary] = await Promise.all([
      googleMaps.importLibrary("maps"),
      googleMaps.importLibrary("marker"),
      googleMaps.importLibrary("core"),
    ]);
    libraries = {
      Map: mapsLibrary.Map,
      InfoWindow: mapsLibrary.InfoWindow,
      Marker: markerLibrary.Marker,
      LatLngBounds: coreLibrary.LatLngBounds,
      Size: coreLibrary.Size,
      Point: coreLibrary.Point,
      SymbolPath: googleMaps.SymbolPath,
      event: googleMaps.event,
    };
  } else {
    libraries = {
      Map: googleMaps.Map,
      InfoWindow: googleMaps.InfoWindow,
      Marker: googleMaps.Marker,
      LatLngBounds: googleMaps.LatLngBounds,
      Size: googleMaps.Size,
      Point: googleMaps.Point,
      SymbolPath: googleMaps.SymbolPath,
      event: googleMaps.event,
    };
  }
  if (typeof libraries.Map !== "function" || typeof libraries.Marker !== "function") {
    throw new Error("Google Maps loaded without its Map and Marker constructors");
  }
  return libraries;
}

function loadGoogleMaps(apiKey) {
  if (window.google?.maps?.importLibrary) return getGoogleMapsLibraries();
  if (googleMapsPromise && googleMapsPromiseKey === apiKey) return googleMapsPromise;

  googleMapsPromiseKey = apiKey;
  googleMapsPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    // Load the standard Maps JS API namespace. The app supports both its
    // classic constructors and importLibrary, so it doesn't depend on the
    // optional importLibrary bootstrap being installed by the script.
    const query = new URLSearchParams({ key: apiKey, v: "weekly" });
    script.src = `https://maps.googleapis.com/maps/api/js?${query.toString()}`;
    script.async = true;
    script.defer = true;
    script.onload = () => getGoogleMapsLibraries().then(resolve, reject);
    script.onerror = () => reject(new Error("Google Maps API could not be loaded"));
    document.head.appendChild(script);
  }).catch((error) => {
    if (googleMapsPromiseKey === apiKey) googleMapsPromise = null;
    throw error;
  });

  return googleMapsPromise;
}

export function loadConfiguredGoogleMaps() {
  if (!GOOGLE_MAPS_API_KEY) {
    return Promise.reject(new Error("Google Maps API key is not configured"));
  }
  return loadGoogleMaps(GOOGLE_MAPS_API_KEY);
}

function LeafletViewport({ center, zoom, fitPositions, mapRef }) {
  const map = useMap();
  const centerLat = center[0];
  const centerLng = center[1];
  const fitSignature = fitPositions.filter(Boolean).map(([latitude, longitude]) => `${latitude},${longitude}`).join("|");

  useEffect(() => {
    mapRef.current = map;
    return () => { mapRef.current = null; };
  }, [map, mapRef]);

  useEffect(() => {
    const points = fitPositions.filter(Boolean);
    if (points.length > 1) map.fitBounds(points, { padding: [52, 52], maxZoom: 14, animate: false });
    else if (points.length === 1) map.setView(points[0], zoom, { animate: false });
    else map.setView([centerLat, centerLng], zoom, { animate: false });
    // The signature tracks coordinate changes while avoiding refits on fresh array literals.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centerLat, centerLng, fitSignature, map, zoom]);

  return null;
}

function LeafletMap({ center, zoom, markers, fitPositions, className, scrollWheelZoom, mapRef }) {
  return (
    <MapContainer center={center} zoom={zoom} scrollWheelZoom={scrollWheelZoom} zoomControl={false} className={className}>
      <LeafletViewport center={center} zoom={zoom} fitPositions={fitPositions} mapRef={mapRef} />
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      {markers.map((marker, index) => {
        if (!marker.position) return null;
        const key = `${marker.label}-${index}`;
        if (marker.vehicle) {
          return (
            <Marker key={key} position={marker.position} title={marker.label} icon={leafletVehicleIcon}>
              <Popup><span className="text-slate-900">{marker.label}</span></Popup>
            </Marker>
          );
        }
        if (marker.callout) {
          const calloutIcon = new L.DivIcon({
            html: pickupMarkerSvg(marker.callout),
            className: "",
            iconSize: [PICKUP_MARKER_SIZE, PICKUP_MARKER_HEIGHT],
            iconAnchor: [PICKUP_MARKER_SIZE / 2, PICKUP_MARKER_HEIGHT],
            popupAnchor: [0, -PICKUP_MARKER_HEIGHT + 12],
          });
          return (
            <Marker key={key} position={marker.position} title={marker.label} icon={calloutIcon}>
              <Popup><span className="text-slate-900">{marker.label}</span></Popup>
            </Marker>
          );
        }
        return (
          <CircleMarker
            key={key}
            center={marker.position}
            radius={marker.radius || 9}
            pathOptions={{ color: "#ffffff", weight: 3, fillColor: marker.color || "#fbbf24", fillOpacity: 1 }}
          >
            <Popup><span className="text-slate-900">{marker.label}</span></Popup>
          </CircleMarker>
        );
      })}
    </MapContainer>
  );
}

function markerElement(item) {
  const element = document.createElement("div");
  element.style.lineHeight = "0";
  if (item.vehicle) {
    element.innerHTML = VEHICLE_MARKER_SVG;
    element.style.width = "44px";
    element.style.height = "44px";
    return { element, anchor: "center" };
  }
  if (item.person) {
    element.innerHTML = PERSON_MARKER_SVG;
    element.style.width = "42px";
    element.style.height = "50px";
    return { element, anchor: "bottom" };
  }
  if (item.callout) {
    element.innerHTML = pickupMarkerSvg(item.callout);
    element.style.width = `${PICKUP_MARKER_SIZE}px`;
    element.style.height = `${PICKUP_MARKER_HEIGHT}px`;
    return { element, anchor: "bottom" };
  }
  const radius = item.radius ? Math.max(7, item.radius) : 9;
  element.style.width = `${radius * 2}px`;
  element.style.height = `${radius * 2}px`;
  element.style.borderRadius = "999px";
  element.style.background = item.color || "#fbbf24";
  element.style.border = "3px solid #ffffff";
  element.style.boxSizing = "border-box";
  return { element, anchor: "center" };
}

function MapboxRideMap({
  center,
  zoom = 12,
  markers = [],
  fitPositions = [],
  focusPosition = null,
  focusRequestId = 0,
  className = "h-full w-full",
  scrollWheelZoom = false,
  ariaLabel = "Map",
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const leafletMapRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    loadMapbox()
      .then((mapboxgl) => {
        if (!active || !containerRef.current || mapRef.current) return;
        const container = containerRef.current;
        container.style.position = "absolute";
        container.style.inset = "0";
        container.style.width = "100%";
        container.style.height = "100%";
        mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
        const map = new mapboxgl.Map({
          container,
          style: MAPBOX_STYLE,
          center: [Number(center[1]), Number(center[0])],
          zoom,
          cooperativeGestures: !scrollWheelZoom,
          attributionControl: true,
        });
        mapRef.current = map;
        map.on("load", () => {
          const canvas = map.getCanvas();
          canvas.style.maxWidth = "none";
          map.resize();
          if (active) setReady(true);
        });
      })
      .catch((error) => {
        console.warn("Mapbox could not be loaded; using OpenStreetMap instead.", error);
        if (active) setFailed(true);
      });
    return () => {
      active = false;
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];
      mapRef.current?.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fitSignature = useMemo(() => fitPositions
    .filter(Boolean)
    .map(([latitude, longitude]) => `${latitude},${longitude}`)
    .join("|"), [fitPositions]);
  const markersSignature = useMemo(() => markers
    .filter((marker) => marker.position)
    .map((marker) => `${marker.position[0]},${marker.position[1]},${marker.label},${marker.color || "#fbbf24"},${marker.callout?.etaMinutes ?? marker.callout?.label ?? ""},${marker.callout?.unit ?? ""},${marker.person ? "person" : ""}`)
    .join("|"), [markers]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    const points = fitPositions.filter((position) => position && position.length >= 2);
    if (points.length > 1) {
      const bounds = new window.mapboxgl.LngLatBounds();
      points.forEach(([latitude, longitude]) => bounds.extend([Number(longitude), Number(latitude)]));
      map.fitBounds(bounds, { padding: 52, maxZoom: 14, duration: 0 });
    } else {
      const position = points[0] || center;
      map.jumpTo({ center: [Number(position[1]), Number(position[0])], zoom });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center[0], center[1], fitSignature, ready, zoom]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || !focusPosition || focusPosition.length < 2) return;
    map.flyTo({
      center: [Number(focusPosition[1]), Number(focusPosition[0])],
      zoom: Math.max(map.getZoom() || zoom, 15),
      duration: 500,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusRequestId, ready]);

  useEffect(() => {
    const map = mapRef.current;
    const mapboxgl = window.mapboxgl;
    if (!map || !ready || !mapboxgl) return undefined;
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = markers.filter((item) => item.position).map((item) => {
      const { element, anchor } = markerElement(item);
      const marker = new mapboxgl.Marker({ element, anchor })
        .setLngLat([Number(item.position[1]), Number(item.position[0])])
        .setPopup(new mapboxgl.Popup({ offset: 18, closeButton: false }).setText(item.label || ""))
        .addTo(map);
      return marker;
    });
    return () => {
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markersSignature, ready]);

  const zoomMap = (direction) => {
    const map = mapRef.current;
    if (!map) return;
    map.easeTo({ zoom: (map.getZoom() || zoom) + direction, duration: 200 });
  };

  const locateMap = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(({ coords }) => {
      if (mapRef.current) mapRef.current.flyTo({ center: [coords.longitude, coords.latitude], zoom: 16, duration: 500 });
      else leafletMapRef.current?.flyTo([coords.latitude, coords.longitude], 16, { duration: 0.5 });
    });
  };

  if (failed) {
    return (
      <div className={className}>
        <div className="relative h-full w-full">
          <LeafletMap center={center} zoom={zoom} markers={markers} fitPositions={fitPositions} className="absolute inset-0 h-full w-full" scrollWheelZoom={scrollWheelZoom} mapRef={leafletMapRef} />
          <MapControls onZoomIn={() => leafletMapRef.current?.zoomIn()} onZoomOut={() => leafletMapRef.current?.zoomOut()} onLocate={locateMap} />
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="relative h-full w-full">
        <div ref={containerRef} className="absolute inset-0 h-full w-full" role="application" aria-label={ariaLabel} />
        <MapControls onZoomIn={() => zoomMap(1)} onZoomOut={() => zoomMap(-1)} onLocate={locateMap} />
      </div>
    </div>
  );
}

function LegacyGoogleMapView({
  center,
  zoom = 12,
  markers = [],
  fitPositions = [],
  focusPosition = null,
  focusRequestId = 0,
  className = "h-full w-full",
  scrollWheelZoom = false,
  ariaLabel = "Map",
}) {
  const mapElement = useRef(null);
  const mapInstance = useRef(null);
  const markerInstances = useRef([]);
  const leafletMapInstance = useRef(null);
  const [mapsApi, setMapsApi] = useState(null);
  const [useGoogleMaps, setUseGoogleMaps] = useState(false);

  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY) return undefined;
    let active = true;
    loadGoogleMaps(GOOGLE_MAPS_API_KEY)
      .then((api) => {
        if (active) {
          setMapsApi(api);
          setUseGoogleMaps(true);
        }
      })
      .catch((error) => {
        console.warn("Google Maps could not be loaded; using OpenStreetMap instead.", error);
        if (active) {
          setUseGoogleMaps(false);
        }
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!useGoogleMaps || !mapsApi || !mapElement.current) return undefined;
    const maps = mapsApi;
    mapInstance.current = new maps.Map(mapElement.current, {
      center: { lat: Number(center[0]), lng: Number(center[1]) },
      zoom,
      colorScheme: "LIGHT",
      disableDefaultUI: true,
      clickableIcons: false,
      fullscreenControl: false,
      mapTypeControl: false,
      streetViewControl: false,
      keyboardShortcuts: false,
      zoomControl: false,
      gestureHandling: scrollWheelZoom ? "auto" : "cooperative",
    });
    return () => {
      markerInstances.current.forEach(({ marker, listener, infoWindow }) => {
        infoWindow?.close();
        if (listener) maps.event.removeListener(listener);
        marker.setMap(null);
      });
      markerInstances.current = [];
      mapInstance.current = null;
    };
    // Map creation happens once per mounted map; center and markers are updated below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapsApi, useGoogleMaps]);

  const fitSignature = useMemo(() => fitPositions
    .filter(Boolean)
    .map(([latitude, longitude]) => `${latitude},${longitude}`)
    .join("|"), [fitPositions]);
  const markersSignature = useMemo(() => markers
    .filter((marker) => marker.position)
    .map((marker) => `${marker.position[0]},${marker.position[1]},${marker.label},${marker.color || "#fbbf24"},${marker.callout?.etaMinutes ?? marker.callout?.label ?? ""},${marker.callout?.unit ?? ""},${marker.person ? "person" : ""}`)
    .join("|"), [markers]);

  useEffect(() => {
    const map = mapInstance.current;
    const maps = mapsApi;
    if (!map || !maps) return;

    const points = fitPositions.filter((position) => position && position.length >= 2);
    if (points.length > 1) {
      const bounds = new maps.LatLngBounds();
      points.forEach(([latitude, longitude]) => bounds.extend({ lat: Number(latitude), lng: Number(longitude) }));
      map.fitBounds(bounds, 52);
    } else {
      const position = points[0] || center;
      map.setCenter({ lat: Number(position[0]), lng: Number(position[1]) });
      map.setZoom(zoom);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center[0], center[1], fitSignature, mapsApi, useGoogleMaps, zoom]);

  useEffect(() => {
    if (!focusPosition || focusPosition.length < 2) return;
    const position = [Number(focusPosition[0]), Number(focusPosition[1])];
    if (useGoogleMaps) {
      const map = mapInstance.current;
      if (!map) return;
      map.setCenter({ lat: position[0], lng: position[1] });
      map.setZoom(Math.max(map.getZoom() || zoom, 15));
      return;
    }
    const map = leafletMapInstance.current;
    if (map) map.flyTo(position, Math.max(map.getZoom(), 15), { duration: 0.5 });
    // This effect is intentionally driven by the button request id so the same
    // pickup coordinates can be focused repeatedly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusRequestId, mapsApi, useGoogleMaps]);

  useEffect(() => {
    const map = mapInstance.current;
    const maps = mapsApi;
    if (!map || !maps) return undefined;

    markerInstances.current.forEach(({ marker, listener, infoWindow }) => {
      infoWindow?.close();
      if (listener) maps.event.removeListener(listener);
      marker.setMap(null);
    });
    markerInstances.current = markers.filter((item) => item.position).map((item) => {
      const marker = new maps.Marker({
        map,
        position: { lat: Number(item.position[0]), lng: Number(item.position[1]) },
        title: item.label,
        icon: item.vehicle
          ? {
            url: VEHICLE_MARKER_URL,
            scaledSize: new maps.Size(44, 44),
            anchor: new maps.Point(22, 22),
          }
          : item.callout
            ? {
              url: pickupMarkerUrl(item.callout),
              scaledSize: new maps.Size(PICKUP_MARKER_SIZE, PICKUP_MARKER_HEIGHT),
              anchor: new maps.Point(PICKUP_MARKER_SIZE / 2, PICKUP_MARKER_HEIGHT),
            }
          : {
            path: maps.SymbolPath.CIRCLE,
            scale: item.radius ? Math.max(7, item.radius) : 9,
            fillColor: item.color || "#fbbf24",
            fillOpacity: 1,
            strokeColor: "#ffffff",
            strokeWeight: 3,
          },
      });
      const infoContent = item.label ? document.createElement("div") : null;
      if (infoContent) {
        infoContent.textContent = item.label;
        infoContent.style.color = "#0f172a";
        infoContent.style.fontFamily = "'Plus Jakarta Sans', sans-serif";
        infoContent.style.fontSize = "14px";
        infoContent.style.fontWeight = "600";
        infoContent.style.lineHeight = "1.4";
      }
      const infoWindow = infoContent ? new maps.InfoWindow({ content: infoContent }) : null;
      const listener = infoWindow ? marker.addListener("click", () => infoWindow.open({ anchor: marker, map })) : null;
      return { marker, listener, infoWindow };
    });

    return () => {
      markerInstances.current.forEach(({ marker, listener, infoWindow }) => {
        infoWindow?.close();
        if (listener) maps.event.removeListener(listener);
        marker.setMap(null);
      });
      markerInstances.current = [];
    };
    // The signature keeps markers in sync without rebuilding for fresh array literals.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapsApi, markersSignature, useGoogleMaps]);

  const zoomMap = (direction) => {
    if (useGoogleMaps) {
      const map = mapInstance.current;
      if (map) map.setZoom((map.getZoom() || zoom) + direction);
    } else {
      const map = leafletMapInstance.current;
      if (map) direction > 0 ? map.zoomIn() : map.zoomOut();
    }
  };

  const locateMap = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(({ coords }) => {
      const map = useGoogleMaps ? mapInstance.current : leafletMapInstance.current;
      if (!map) return;
      const position = [coords.latitude, coords.longitude];
      if (useGoogleMaps) {
        map.setCenter({ lat: position[0], lng: position[1] });
        map.setZoom(16);
      } else {
        map.flyTo(position, 16, { duration: 0.5 });
      }
    });
  };

  return (
    <div className={className}>
      <div className="relative h-full w-full">
        {useGoogleMaps && mapsApi
          ? <div ref={mapElement} className="absolute inset-0" role="application" aria-label={ariaLabel} />
          : <LeafletMap center={center} zoom={zoom} markers={markers} fitPositions={fitPositions} className="absolute inset-0 h-full w-full" scrollWheelZoom={scrollWheelZoom} mapRef={leafletMapInstance} />}
        <MapControls onZoomIn={() => zoomMap(1)} onZoomOut={() => zoomMap(-1)} onLocate={locateMap} />
      </div>
    </div>
  );
}

export default function GoogleMapView(props) {
  if (MAPBOX_ACCESS_TOKEN) return <MapboxRideMap {...props} />;
  return <LegacyGoogleMapView {...props} />;
}

export function MapControls({ onZoomIn, onZoomOut, onLocate }) {
  const { t } = useTranslation();
  const buttonClass = "flex h-20 w-20 items-center justify-center text-slate-900 transition hover:bg-slate-100 active:bg-slate-200 focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-amber-400";
  const stopMapClick = (action) => (event) => {
    event.stopPropagation();
    action();
  };

  return (
    <div className="absolute right-4 top-1/2 z-[500] flex -translate-y-1/2 flex-col items-center gap-3" onDoubleClick={(event) => event.stopPropagation()}>
      <div className="overflow-hidden rounded-[2.25rem] bg-white shadow-[0_2px_12px_rgba(15,23,42,.2)]">
        <button type="button" onClick={stopMapClick(onZoomIn)} aria-label="Zoom in" className={buttonClass}><Plus className="h-8 w-8" strokeWidth={2} /></button>
        <div className="mx-2 border-t border-slate-200" />
        <button type="button" onClick={stopMapClick(onZoomOut)} aria-label="Zoom out" className={buttonClass}><Minus className="h-8 w-8" strokeWidth={2} /></button>
      </div>
      <button type="button" onClick={stopMapClick(onLocate)} aria-label={t("rides.use_my_location")} title={t("rides.use_my_location")} className="flex h-20 w-20 items-center justify-center rounded-full bg-white text-slate-900 shadow-[0_2px_12px_rgba(15,23,42,.2)] transition hover:bg-slate-100 active:bg-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400">
        <Navigation className="h-8 w-8 fill-current" strokeWidth={1.8} />
      </button>
    </div>
  );
}
